import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Clock, Package, RefreshCw } from 'lucide-react';
import { equipmentBookingAPI } from '../../lib/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EquipmentOpsDashboard = () => {
  const [ops, setOps] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOps = async () => {
    setLoading(true);
    try {
      const res = await equipmentBookingAPI.getOpsDashboard();
      setOps(res.data.data);
    } catch {
      toast.error('Failed to load operations data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOps(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await equipmentBookingAPI.updateStatus(id, { status });
      toast.success(`Marked as ${status.replace('_', ' ')}`);
      fetchOps();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  const { summary, todayPickups, todayReturns } = ops || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Equipment Operations</h1>
          <p className="page-subtitle">Today's pickups, returns, and active rentals</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchOps}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Pending Confirmation', value: summary?.pending || 0, icon: Clock, color: '#D97706', bg: '#FFFBEB' },
          { label: 'Currently Out', value: summary?.active || 0, icon: Package, color: '#2563EB', bg: '#EFF6FF' },
          { label: 'Overdue Returns', value: summary?.overdue || 0, icon: AlertCircle, color: '#DC2626', bg: '#FEF2F2' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div className="stat-card" key={label}>
            <div className="stat-icon" style={{ background: bg }}><Icon size={22} color={color} /></div>
            <div><div className="stat-value" style={{ color }}>{value}</div><div className="stat-label">{label}</div></div>
          </div>
        ))}
      </div>

      {summary?.overdue > 0 && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10, color: '#DC2626' }}>
          <AlertCircle size={18} />
          <span style={{ fontWeight: 500 }}>{summary.overdue} equipment item(s) are overdue for return. Guests have been notified via SMS.</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Today's Pickups */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Today's Pickups</span>
            <span className="badge badge-blue">{(todayPickups || []).length}</span>
          </div>
          {(todayPickups || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
              <CheckCircle size={32} style={{ margin: '0 auto 8px' }} />
              <p>No pickups scheduled today</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {todayPickups.map(b => (
                <div key={b._id} style={{ padding: 14, background: '#EFF6FF', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{b.equipment?.name}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                      {b.guest?.firstName} {b.guest?.lastName} · {b.guest?.phone}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      Booking: {b.bookingNumber} · Qty: {b.quantity}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-primary" onClick={() => updateStatus(b._id, 'picked_up')}>
                    Confirm Pickup
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Returns */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Today's Returns</span>
            <span className="badge badge-green">{(todayReturns || []).length}</span>
          </div>
          {(todayReturns || []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
              <CheckCircle size={32} style={{ margin: '0 auto 8px' }} />
              <p>No returns scheduled today</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {todayReturns.map(b => (
                <div key={b._id} style={{ padding: 14, background: '#ECFDF5', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{b.equipment?.name}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                      {b.guest?.firstName} {b.guest?.lastName} · {b.guest?.phone}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      Due: {format(new Date(b.endDate), 'h:mm a')} · Qty: {b.quantity}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-success" onClick={() => updateStatus(b._id, 'returned')}>
                    Confirm Return
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentOpsDashboard;
